/**
 * 增强版微信分享工具
 * 解决权限验证失败问题，提供更好的错误处理和重试机制
 */

import { isWechatBrowser } from './wechatEnvironment'
import { getWechatJsConfig } from '@/api/improvedWechat'
import { updateMetaTags } from './metaTags'
import { logShareData } from '@/api/share'

// 全局状态管理，防止重复初始化
const globalShareState = {
  isInitializing: false,
  isInitialized: false,
  lastInitTime: 0,
  initPromise: null,
  configCount: 0,
  maxConfigsPerMinute: 60, // 每分钟最多配置60次
  visibilityListenerAdded: false, // 防止重复添加可见性监听器
  urlChangeListenerAdded: false, // 防止重复添加URL变化监听器
  lastUrl: '', // 记录上次的URL
  urlChangeTimeout: null, // URL变化防抖定时器
  pendingRequests: new Map(), // 正在进行的请求去重
  configCache: new Map(), // 配置缓存
  lastSuccessfulUrl: '', // 最后一次成功配置的URL
  lastSuccessfulTime: 0, // 最后一次成功配置的时间
  currentShareData: null // 当前页面的分享数据，用于监听器重新初始化时使用
}

/**
 * 检查是否可以进行新的初始化
 */
function canInitialize(newShareData = null) {
  const now = Date.now()
  const oneMinute = 60 * 1000
  
  // 如果正在初始化，返回false
  if (globalShareState.isInitializing) {
    console.log('微信分享正在初始化中，跳过重复请求')
    return false
  }
  
  // 如果传入了新的分享数据，检查是否与当前分享数据不同
  if (newShareData && globalShareState.currentShareData) {
    const currentData = globalShareState.currentShareData
    const hasDataChanged = (
      newShareData.title !== currentData.title ||
      newShareData.desc !== currentData.desc ||
      newShareData.link !== currentData.link ||
      newShareData.imgUrl !== currentData.imgUrl
    )
    
    if (hasDataChanged) {
      console.log('检测到分享数据变化，允许重新初始化:', {
        新数据: newShareData,
        当前数据: currentData
      })
      return true
    }
  }
  
  // 如果已经初始化且在1分钟内，返回false
  if (globalShareState.isInitialized && (now - globalShareState.lastInitTime) < oneMinute) {
    console.log('微信分享已在1分钟内初始化过，跳过重复请求')
    return false
  }
  
  // 检查频率限制
  if (globalShareState.configCount >= globalShareState.maxConfigsPerMinute) {
    const timeSinceLastInit = now - globalShareState.lastInitTime
    if (timeSinceLastInit < oneMinute) {
      console.log('微信分享配置频率过高，跳过请求')
      return false
    } else {
      // 重置计数器
      globalShareState.configCount = 0
    }
  }
  
  return true
}

/**
 * 标记初始化开始
 */
function markInitializationStart() {
  globalShareState.isInitializing = true
  globalShareState.configCount++
  globalShareState.lastInitTime = Date.now()
}

/**
 * 标记初始化完成
 */
function markInitializationComplete(success = true) {
  globalShareState.isInitializing = false
  if (success) {
    globalShareState.isInitialized = true
  }
}

/**
 * 确保URL是绝对路径
 * @param {string} url - 需要处理的URL
 * @returns {string} - 绝对路径URL
 */
function ensureAbsoluteUrl(url) {
  if (!url) return 'https://mall.sainbainu.com/static/images/sharelogo.png'
  if (url.startsWith('http')) return url
  if (url.startsWith('/')) return `${window.location.origin}${url}`
  return `${window.location.origin}/${url}`
}

/**
 * 标准化URL处理 - 修复签名验证问题
 * @param {string} url - 原始URL
 * @returns {string} - 标准化后的URL
 */
function normalizeUrlForWechat(url) {
  try {
    // 使用传入的URL或当前页面URL
    const targetUrl = url || window.location.href;
    
    // 创建URL对象进行标准化处理
    const urlObj = new URL(targetUrl);
    
    // 构建标准化的URL：协议 + 主机 + 路径 + 查询参数（不包含hash）
    const normalizedUrl = `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}${urlObj.search}`;
    
    console.log('URL标准化处理:', {
      原始URL: targetUrl,
      标准化URL: normalizedUrl,
      协议: urlObj.protocol,
      主机: urlObj.host,
      路径: urlObj.pathname,
      查询参数: urlObj.search,
      hash: urlObj.hash
    });
    
    return normalizedUrl;
  } catch (error) {
    console.error('URL标准化处理失败:', error);
    // 降级处理：移除hash但保留查询参数
    const fallbackUrl = (url || window.location.href).split('#')[0];
    console.log('使用降级URL处理:', fallbackUrl);
    return fallbackUrl;
  }
}

/**
 * 加载微信JS-SDK脚本（增强版）
 * @returns {Promise<boolean>}
 */
export async function loadWechatJsSDKEnhanced() {
  return new Promise((resolve, reject) => {
    // 检查是否已经加载
    if (typeof window.wx !== 'undefined') {
      console.log('微信JS-SDK已存在')
      resolve(true)
      return
    }
    
    // 检查是否已有脚本标签
    const existingScript = document.querySelector('script[src*="jweixin"]')
    if (existingScript) {
      console.log('微信JS-SDK脚本已存在，等待加载完成')
      existingScript.onload = () => {
        console.log('微信JS-SDK脚本加载完成')
        resolve(true)
      }
      existingScript.onerror = () => {
        console.error('微信JS-SDK脚本加载失败')
        reject(new Error('微信JS-SDK脚本加载失败'))
      }
      return
    }
    
    // 创建并加载脚本
    const script = document.createElement('script')
    script.src = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js'
    script.onload = () => {
      console.log('微信JS-SDK脚本加载成功')
      // 等待一段时间确保脚本完全初始化
      setTimeout(() => {
        resolve(true)
      }, 200)
    }
    script.onerror = () => {
      console.error('微信JS-SDK脚本加载失败')
      reject(new Error('微信JS-SDK脚本加载失败'))
    }
    
    document.head.appendChild(script)
  })
}

/**
 * 获取微信JS-SDK配置（增强版）
 * @param {string} url 页面URL
 * @returns {Promise<Object>}
 */
export async function getWechatJsConfigEnhanced(url, shareData = null) {
  try {
    console.log('=== 微信JS-SDK配置获取开始 ===')
    console.log('函数输入参数 url:', url)
    console.log('当前页面完整URL:', window.location.href)
    
    // 使用新的URL标准化函数
    const currentUrl = normalizeUrlForWechat(url)
    console.log('标准化后的URL:', currentUrl)
    
    // 检查缓存
    const cacheKey = currentUrl
    const now = Date.now()
    const cacheEntry = globalShareState.configCache.get(cacheKey)
    
    // 如果缓存存在且未过期（5分钟内），直接返回缓存
    if (cacheEntry && (now - cacheEntry.timestamp) < 5 * 60 * 1000) {
      console.log('✅ 使用缓存的配置:', cacheEntry.config)
      return cacheEntry.config
    }
    
    // 检查是否有相同URL的请求正在进行
    if (globalShareState.pendingRequests.has(cacheKey)) {
      console.log('⏳ 等待相同URL的请求完成...')
      return await globalShareState.pendingRequests.get(cacheKey)
    }
    
    // 创建新的请求Promise
    const requestPromise = (async () => {
      try {
        // 频率限制检查
        const timeSinceLastSuccess = now - globalShareState.lastSuccessfulTime
        if (globalShareState.lastSuccessfulUrl === currentUrl && timeSinceLastSuccess < 2000) {
          console.log('⚠️ 请求过于频繁，使用上次成功的配置')
          const lastConfig = globalShareState.configCache.get(globalShareState.lastSuccessfulUrl)
          if (lastConfig) {
            return lastConfig.config
          }
        }
        
        console.log('准备调用getWechatJsConfig API...')
        console.log('API请求URL参数:', currentUrl)
        console.log('API请求分享参数:', shareData)
        console.log('API请求时间:', new Date().toISOString())
        
        // 修改这里：确保正确传递 shareData 参数到 getWechatJsConfig
        const apiStartTime = Date.now()
        const response = await getWechatJsConfig(currentUrl, shareData)
        const apiEndTime = Date.now()
        const apiDuration = apiEndTime - apiStartTime
        
        console.log('=== API调用完成 ===')
        console.log('API调用耗时:', apiDuration + 'ms')
        console.log('API响应时间:', new Date().toISOString())
        console.log('API原始响应:', response)
        
        let finalConfig = null
        
        if (response && response.data) {
          console.log('✅ 响应包含data字段，处理response.data')
          finalConfig = {
            ...response.data,
            cleanUrl: currentUrl
          }
          
          // 如果服务端返回了分享参数，记录并添加到配置中
          if (response.data.share_info) {
            console.log('✅ 服务端返回了分享参数:', response.data.share_info)
            finalConfig.serverShareData = response.data.share_info
          }
        } else if (response && (response.code === 0 || response.code === 200)) {
          console.log('✅ 响应code为成功状态，处理response')
          finalConfig = {
            ...response,
            cleanUrl: currentUrl
          }
          
          // 如果服务端返回了分享参数，记录并添加到配置中
          if (response.share_info) {
            console.log('✅ 服务端返回了分享参数:', response.share_info)
            finalConfig.serverShareData = response.share_info
          }
        } else {
          throw new Error(response?.msg || '获取微信JS-SDK配置失败')
        }
        
        // 缓存成功的配置
        globalShareState.configCache.set(cacheKey, {
          config: finalConfig,
          timestamp: now
        })
        
        // 更新最后成功的记录
        globalShareState.lastSuccessfulUrl = currentUrl
        globalShareState.lastSuccessfulTime = now
        
        // 清理过期缓存（保留最近10个）
        if (globalShareState.configCache.size > 10) {
          const entries = Array.from(globalShareState.configCache.entries())
          entries.sort((a, b) => b[1].timestamp - a[1].timestamp)
          globalShareState.configCache.clear()
          entries.slice(0, 10).forEach(([key, value]) => {
            globalShareState.configCache.set(key, value)
          })
        }
        
        console.log('最终配置对象:', finalConfig)
        console.log('=== 微信JS-SDK配置获取成功 ===')
        
        return finalConfig
      } finally {
        // 清理pending请求
        globalShareState.pendingRequests.delete(cacheKey)
      }
    })()
    
    // 将请求Promise存储到pending中
    globalShareState.pendingRequests.set(cacheKey, requestPromise)
    
    return await requestPromise
  } catch (error) {
    console.error('获取微信JS-SDK配置失败:', error)
    console.error('错误详情:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: url || window.location.href
    })
    
    // 在开发环境或网络错误时，返回默认配置以便继续测试
    if (process.env.NODE_ENV === 'development' || 
        (error.message && (error.message.includes('Network Error') || 
                          error.message.includes('网络错误') ||
                          error.message.includes('timeout')))) {
      console.warn('网络错误或开发环境，使用默认配置继续执行')
      return {
        appId: process.env.VUE_APP_WECHAT_MP_APP_ID || 'wx_default_app_id',
        timestamp: Math.floor(Date.now() / 1000),
        nonceStr: Math.random().toString(36).substr(2, 10),
        signature: 'default_signature',
        jsApiList: [
          'updateAppMessageShareData',
          'updateTimelineShareData',
          'onMenuShareTimeline',
          'onMenuShareAppMessage'
        ]
      }
    }
    
    // 为不同类型的错误提供更具体的错误信息
    if (error.response?.status === 404) {
      throw new Error('微信配置API接口不存在，请检查后端配置')
    } else if (error.response?.status === 500) {
      throw new Error('服务器内部错误，请稍后重试')
    } else if (error.message && error.message.includes('timeout')) {
      throw new Error('网络请求超时，请检查网络连接')
    } else {
      throw new Error(`获取微信配置失败: ${error.message || '未知错误'}`)
    }
  }
}

/**
 * 初始化微信JS-SDK（增强版）
 * @param {string} url 页面URL
 * @param {boolean} retryWithSimpleUrl 是否在失败时使用简化URL重试
 * @returns {Promise<boolean>}
 */
export async function initWechatJsSDKEnhanced(url, retryWithSimpleUrl = true, shareData = null) {
  console.log('=== 开始微信JS-SDK初始化 ===')
  console.log('初始化参数:', { url, retryWithSimpleUrl })
  console.log('当前环境信息:', {
    userAgent: navigator.userAgent,
    isWechat: isWechatBrowser(),
    currentUrl: window.location.href,
    timestamp: new Date().toISOString()
  })

  if (!isWechatBrowser()) {
    console.warn('不在微信环境中，跳过JS-SDK初始化')
    return false
  }

  // 确保微信JS-SDK已加载
  await loadWechatJsSDKEnhanced()
  console.log('微信JS-SDK脚本加载完成')

  // 使用标准化URL处理
  const cleanUrl = normalizeUrlForWechat(url)
  console.log('标准化后的URL:', cleanUrl)

  return new Promise((resolve) => {
    (async () => {
      try {
        console.log('开始获取微信JS-SDK配置...')
        const configData = await getWechatJsConfigEnhanced(cleanUrl, shareData)
        console.log('获取到的配置数据:', configData)

      if (!configData || !configData.signature) {
        console.error('配置数据无效:', configData)
        throw new Error('获取微信配置失败：配置数据无效')
      }

      // 构建最终配置URL - 确保与前端请求的URL完全一致
      let finalUrl = cleanUrl
      if (configData.url && configData.url !== cleanUrl) {
        console.warn('⚠️ 服务端返回了不同的URL:', {
          前端请求URL: cleanUrl,
          服务端返回URL: configData.url,
          当前页面URL: window.location.href
        })
        // 优先使用前端标准化的URL，确保一致性
        finalUrl = cleanUrl
      }

      console.log('最终配置URL:', finalUrl)
      console.log('URL一致性检查:', {
        前端标准化URL: cleanUrl,
        服务端返回URL: configData.url || '未返回',
        最终使用URL: finalUrl,
        URL是否一致: !configData.url || configData.url === cleanUrl
      })
      console.log('配置参数详情:', {
        appId: configData.appId,
        timestamp: configData.timestamp,
        nonceStr: configData.nonceStr,
        signature: configData.signature ? configData.signature.substring(0, 10) + '...' : 'null',
        jsApiList: configData.jsApiList || ['updateAppMessageShareData', 'updateTimelineShareData', 'onMenuShareAppMessage', 'onMenuShareTimeline']
      })

      // 配置微信JS-SDK
      window.wx.config({
        debug: process.env.NODE_ENV === 'development', // 开发环境开启调试
        appId: configData.appId,
        timestamp: configData.timestamp,
        nonceStr: configData.nonceStr,
        signature: configData.signature,
        jsApiList: configData.jsApiList || [
          'updateAppMessageShareData',
          'updateTimelineShareData',
          'onMenuShareAppMessage',
          'onMenuShareTimeline'
        ]
      })

      console.log('微信JS-SDK配置已提交，等待回调...')

      // 设置超时处理
      const timeoutId = setTimeout(() => {
        console.warn('微信JS-SDK配置超时（15秒），但继续执行')
        resolve({
          success: true,
          configData: configData,
          serverShareData: configData.serverShareData || null,
          timeout: true
        })
      }, 15000)

      // 成功回调
      window.wx.ready(() => {
        clearTimeout(timeoutId)
        console.log('✅ 微信JS-SDK配置成功！')
        console.log('可用的微信API:', {
          updateAppMessageShareData: typeof window.wx.updateAppMessageShareData === 'function',
          updateTimelineShareData: typeof window.wx.updateTimelineShareData === 'function',
          onMenuShareAppMessage: typeof window.wx.onMenuShareAppMessage === 'function',
          onMenuShareTimeline: typeof window.wx.onMenuShareTimeline === 'function'
        })
        
        // 返回配置数据，包括服务端返回的分享参数
        resolve({
          success: true,
          configData: configData,
          serverShareData: configData.serverShareData || null
        })
      })

      // 错误回调
      window.wx.error(async (res) => {
        clearTimeout(timeoutId)
        console.error('❌ 微信JS-SDK配置失败:', res)
        
        // 特殊处理：微信开发工具中的"成功"消息可能被错误地发送到error回调
        if (navigator.userAgent.includes('wechatdevtools')) {
          console.warn('在微信开发工具中检测到error回调，但可能是正常的')
          
          // 检查是否是"成功"相关的消息
          if (res === '成功' || 
              (typeof res === 'string' && (res.includes('成功') || res.toLowerCase().includes('success'))) ||
              (res && res.errMsg && (res.errMsg.includes('成功') || res.errMsg.toLowerCase().includes('success')))) {
            console.log('微信开发工具返回"成功"但调用了error回调，将其处理为成功')
            resolve({
              success: true,
              configData: configData,
              serverShareData: configData.serverShareData || null,
              devtools: true
            })
            return
          }
          
          // 在微信开发工具中，即使是真正的错误也继续执行
          console.warn('微信开发工具中忽略配置错误，继续执行')
          resolve({
            success: true,
            configData: configData,
            serverShareData: configData.serverShareData || null,
            devtools: true,
            error: res
          })
          return
        }
        
        // 检查是否是签名验证失败（通常由URL不匹配导致）
        const isSignatureError = res && (
          (typeof res === 'string' && res.includes('invalid signature')) ||
          (res.errMsg && res.errMsg.includes('invalid signature')) ||
          (res.errMsg && res.errMsg.includes('config:invalid signature'))
        )
        
        if (isSignatureError && retryWithSimpleUrl) {
          console.warn('🔄 检测到签名验证失败，尝试多种URL格式重试...')
          
          // 定义多种URL格式进行重试
          const retryUrls = [
            // 1. 不带查询参数的简化URL
            `${window.location.protocol}//${window.location.host}${window.location.pathname}`,
            // 2. 只保留scene参数的URL
            `${window.location.protocol}//${window.location.host}${window.location.pathname}?scene=1`,
            // 3. 当前页面的完整URL（去除hash）
            window.location.href.split('#')[0]
          ]
          
          for (let i = 0; i < retryUrls.length; i++) {
            const retryUrl = retryUrls[i]
            if (retryUrl === cleanUrl) {
              continue // 跳过已经尝试过的URL
            }
            
            try {
              console.log(`🔄 尝试第${i + 1}种URL格式:`, retryUrl)
              const retryResult = await initWechatJsSDKEnhanced(retryUrl, false)
              if (retryResult && retryResult.success) {
                console.log(`✅ 使用第${i + 1}种URL格式重试成功:`, retryUrl)
                resolve(retryResult)
                return
              }
            } catch (retryError) {
              console.warn(`❌ 第${i + 1}种URL格式重试失败:`, retryError.message)
            }
          }
          
          console.error('❌ 所有URL格式重试都失败了')
        }
        
        // 构建详细的错误信息
        const errorDetails = {
          errorResponse: res,
          configData: {
            appId: configData.appId,
            timestamp: configData.timestamp,
            nonceStr: configData.nonceStr,
            signature: configData.signature ? configData.signature.substring(0, 10) + '...' : 'null',
            jsApiList: configData.jsApiList
          },
          urlInfo: {
            originalUrl: url,
            cleanUrl: cleanUrl,
            currentUrl: window.location.href,
            finalConfigUrl: finalUrl,
            searchParams: window.location.search,
            hashParams: window.location.hash,
            hostname: window.location.hostname,
            protocol: window.location.protocol,
            port: window.location.port,
            pathname: window.location.pathname,
            isWechat: isWechatBrowser(),
            wechatVersion: navigator.userAgent.match(/MicroMessenger\/([0-9.]+)/)
          },
          // 添加更多调试信息
          debugInfo: {
            timestamp: new Date().toISOString(),
            performanceNow: performance.now(),
            documentReadyState: document.readyState,
            windowLoaded: document.readyState === 'complete'
          }
        }
        
        console.error('微信JS-SDK配置失败详细信息:', errorDetails)
        
        // 报告错误到后端
        try {
          // 从页面meta标签提取默认分享数据
          const defaultShareData = {
            title: document.title || '蒙古丽人商城',
            desc: (document.querySelector('meta[name="description"]')?.content || '专注于蒙古服饰设计、开发、推广。'),
            link: window.location.href,
            imgUrl: (document.querySelector('meta[name="wechat:image"]')?.content || 'https://mall.sainbainu.com/static/images/sharelogo.png')
          }
          
          import('@/api/error').then(({ reportShareError }) => {
            reportShareError('微信JS-SDK配置失败', defaultShareData, {
              operation: 'wechat_js_sdk_config',
              url: cleanUrl,
              errorDetails: errorDetails
            }).catch(err => {
              console.error('报告微信配置错误失败:', err)
            })
          })
        } catch (importError) {
          console.error('导入错误报告模块失败:', importError)
        }
        
        // 在开发环境中，忽略配置错误继续执行
        if (process.env.NODE_ENV === 'development') {
          console.warn('开发环境：忽略微信JS-SDK配置错误，继续执行')
          resolve({
            success: true,
            configData: configData,
            serverShareData: configData.serverShareData || null,
            development: true,
            error: res
          })
          return
        }
        
        // 检查是否是域名配置错误
        if (res && res.errMsg && res.errMsg.includes('invalid url domain')) {
          console.warn('微信JS-SDK域名配置错误')
          console.warn('请在微信公众平台配置以下域名:')
          console.warn('- JS接口安全域名：', window.location.hostname)
          console.warn('- 业务域名：', window.location.hostname)
        }
        
        // 检查是否是签名错误
        if (res && res.errMsg && res.errMsg.includes('invalid signature')) {
          console.error('微信JS-SDK签名验证失败')
          console.error('错误详情:', res)
          console.error('当前URL:', cleanUrl)
          console.error('原始URL:', window.location.href)
          console.error('URL搜索参数:', window.location.search)
          console.error('URL哈希:', window.location.hash)
          console.error('用户代理:', navigator.userAgent)
          console.error('当前域名:', window.location.hostname)
          console.error('当前协议:', window.location.protocol)
          console.error('当前端口:', window.location.port)
          console.error('当前路径:', window.location.pathname)
          console.error('微信环境检测:', isWechatBrowser())
          console.error('微信版本信息:', navigator.userAgent.match(/MicroMessenger\/([0-9.]+)/))
          console.error('配置信息:', configData)
          console.error('可能的原因：')
          console.error('1. URL参数不一致（前端与后端）')
          console.error('2. 时间戳过期')
          console.error('3. 域名配置问题')
          console.error('4. URL编码问题')
          
          // 发送详细的错误信息到服务端用于调试
          try {
            await logShareData({
              type: 'share_error',
              message: '分享功能异常',
              stack: '',
              context: {
                operation: {
                  operation: 'wechat_js_sdk_config',
                  url: cleanUrl,
                  errorDetails: {
                    errorResponse: res,
                    configData: {
                      appId: configData.appId,
                      timestamp: configData.timestamp,
                      nonceStr: configData.nonceStr,
                      signature: configData.signature ? configData.signature.substring(0, 10) + '...' : 'null',
                      jsApiList: configData.jsApiList
                    },
                    urlInfo: {
                      originalUrl: url,
                      cleanUrl: cleanUrl,
                      currentUrl: window.location.href,
                      finalConfigUrl: cleanUrl,
                      searchParams: window.location.search,
                      hashParams: window.location.hash,
                      hostname: window.location.hostname,
                      protocol: window.location.protocol,
                      port: window.location.port,
                      pathname: window.location.pathname,
                      isWechat: isWechatBrowser(),
                      wechatVersion: navigator.userAgent.match(/MicroMessenger\/([0-9.]+)/)
                    },
                    debugInfo: {
                      timestamp: new Date().toISOString(),
                      performanceNow: performance.now(),
                      documentReadyState: document.readyState,
                      windowLoaded: document.readyState === 'complete'
                    }
                  }
                },
                url: cleanUrl,
                timestamp: new Date().toISOString()
              },
              shareData: {
                title: '蒙古丽人商城',
                desc: '专注于蒙古服饰设计、开发、推广。定制热线：133 4714 1793',
                link: cleanUrl,
                imgUrl: 'https://mall.sainbainu.com/static/images/sharelogo.png',
                hasTitle: true,
                hasDesc: true,
                hasLink: true,
                hasImgUrl: true
              },
              wechatInfo: {
                isWechat: isWechatBrowser(),
                isWechatDevTools: false,
                wechatVersion: navigator.userAgent.match(/MicroMessenger\/([0-9.]+)/)?.[1] || 'unknown',
                netType: 'unknown',
                language: navigator.language || 'zh-CN'
              },
              pageInfo: {
                title: document.title,
                url: window.location.href,
                pathname: window.location.pathname,
                search: window.location.search,
                hash: window.location.hash,
                referrer: document.referrer,
                readyState: document.readyState,
                visibilityState: document.visibilityState
              },
              wechatSDKInfo: {
                wxExists: typeof window.wx !== 'undefined',
                wxReady: typeof window.wx !== 'undefined' && window.wx.ready,
                wxConfig: true,
                wxError: true,
                newShareAPI: true,
                oldShareAPI: true
              },
              performanceInfo: {
                loadTime: performance.now(),
                memoryUsage: null
              },
              debugTimestamp: Date.now(),
              domInfo: {
                metaTags: Array.from(document.querySelectorAll('meta')).map(meta => ({
                  name: meta.name || meta.property,
                  content: meta.content
                })),
                titleTag: document.title
              }
            })
            console.log('错误信息已发送到服务端')
          } catch (logError) {
            console.error('发送错误信息失败:', logError)
          }
          console.error('5. 微信环境特殊参数干扰')
          console.error('6. 微信浏览器版本兼容性问题')
          console.error('7. 网络延迟导致的时间同步问题')
          
          // 尝试重新获取配置（使用更简洁的URL）
          if (retryWithSimpleUrl && cleanUrl !== `${window.location.protocol}//${window.location.host}${window.location.pathname}`) {
            console.warn('尝试使用简化URL重新获取配置...')
            const simpleUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`
            console.warn('简化URL:', simpleUrl)
            
            // 使用Promise处理重试逻辑
            console.log('使用简化URL重新初始化微信JS-SDK...')
            initWechatJsSDKEnhanced(simpleUrl, false)
              .then(retryResult => {
                if (retryResult && retryResult.success) {
                  console.log('使用简化URL重新初始化成功')
                  resolve(retryResult)
                } else {
                  // 如果重试也失败，继续原有的错误处理流程
                  console.warn('使用简化URL重试也失败，继续原有流程')
                  console.warn('微信JS-SDK配置失败，但继续执行以保证基本功能')
                  resolve({
                    success: true,
                    configData: configData,
                    serverShareData: configData.serverShareData || null,
                    retryFailed: true,
                    error: res
                  })
                }
              })
              .catch(retryError => {
                console.error('使用简化URL重试失败:', retryError)
                // 继续原有的错误处理流程
                console.warn('微信JS-SDK配置失败，但继续执行以保证基本功能')
                resolve({
                  success: true,
                  configData: configData,
                  serverShareData: configData.serverShareData || null,
                  retryError: retryError,
                  error: res
                })
              })
            return // 重要：在重试时直接返回，避免执行后面的resolve
          }
        }
        
        // 生产环境中，即使配置失败也继续执行，避免完全阻塞分享功能
        console.warn('微信JS-SDK配置失败，但继续执行以保证基本功能')
        resolve({
          success: true,
          configData: configData,
          serverShareData: configData.serverShareData || null,
          production: true,
          error: res
        })
      })
    } catch (error) {
      console.error('初始化微信JS-SDK失败:', error)
      
      // 特殊处理：在微信开发工具中，即使签名失败也继续执行
      if (navigator.userAgent.includes('wechatdevtools')) {
        console.warn('在微信开发工具中，忽略初始化错误继续执行')
        resolve({
          success: true,
          configData: null,
          serverShareData: null,
          devtools: true,
          initError: error
        })
        return
      }
      
      // 在开发环境中，即使初始化失败也继续执行
      if (process.env.NODE_ENV === 'development') {
        console.warn('开发环境：忽略初始化错误，继续执行')
        resolve({
          success: true,
          configData: null,
          serverShareData: null,
          development: true,
          initError: error
        })
        return
      }
      
      resolve({
        success: false,
        configData: null,
        serverShareData: null,
        initError: error
      })
    }
    })()
  })
}

/**
 * 配置微信分享（增强版）
 * @param {Object} shareData 分享数据
 * @returns {Promise<boolean>}
 */


export async function setupWechatShareEnhanced(shareData) {
  console.log('=== 开始设置微信分享 ===')
  console.log('原始分享数据:', shareData)
  console.log('当前页面信息:', {
    title: document.title,
    url: window.location.href,
    metaDescription: document.querySelector('meta[name="description"]')?.content,
    metaWechatImage: document.querySelector('meta[name="wechat:image"]')?.content
  })

  try {
    // 处理分享数据
    const processedShareData = {
      title: shareData.title || document.title || '蒙古丽人商城',
      desc: shareData.desc || shareData.description || '专注于蒙古服饰设计、开发、推广。',
      description: shareData.description || shareData.desc || '专注于蒙古服饰设计、开发、推广。',
      link: shareData.link || window.location.href,
      imgUrl: ensureAbsoluteUrl(shareData.imgUrl || 'https://mall.sainbainu.com/static/images/sharelogo.png')
    }

    console.log('处理后的分享数据:', processedShareData)
    console.log('分享数据验证:', {
      hasTitle: !!processedShareData.title,
      hasDesc: !!processedShareData.desc,
      hasLink: !!processedShareData.link,
      hasImgUrl: !!processedShareData.imgUrl
    })

    // 发送详细的分享数据到服务端用于调试
    try {
      console.log('发送分享数据到服务端进行调试...')
      await logShareData(processedShareData)
      console.log('WechatShareEnhanced: 详细分享数据已发送到服务端')
    } catch (logError) {
      console.error('WechatShareEnhanced: 发送详细分享数据到服务端失败', logError)
    }

    // 确保微信JS-SDK已初始化
    // 关键修复：使用当前页面URL（包含tm参数）进行JS-SDK配置，确保与签名URL一致
    console.log('确保微信JS-SDK已初始化...')
    const initResult = await initWechatJsSDKEnhanced(window.location.href, true, processedShareData)
    
    // 检查是否有服务端返回的分享参数
    let finalShareData = processedShareData
    if (initResult && initResult.serverShareData) {
      console.log('🎯 服务端返回了分享参数:', initResult.serverShareData)
      // 使用服务端返回的分享参数，但保留客户端的备用值
      finalShareData = {
        title: initResult.serverShareData.title || processedShareData.title,
        desc: initResult.serverShareData.desc || processedShareData.desc,
        link: initResult.serverShareData.link || processedShareData.link,
        imgUrl: initResult.serverShareData.imgUrl || processedShareData.imgUrl
      }
      console.log('🎯 最终使用的分享参数:', finalShareData)
    } else {
      console.log('📝 使用客户端处理的分享参数:', finalShareData)
    }

    return new Promise((resolve) => {
      // SDK状态跟踪
      let sdkConfigured = false
      let sdkError = false
      
      // 检查微信JS-SDK是否可用
      if (!window.wx || typeof window.wx.ready !== 'function') {
        console.warn('微信JS-SDK不可用，跳过分享配置')
        resolve({ success: false, finalShareData: finalShareData })
        return
      }

      // 设置较短的超时时间
      const timeoutId = setTimeout(() => {
        console.warn('微信分享配置超时，但继续执行')
        resolve({ success: true, finalShareData: finalShareData }) // 即使超时也认为配置完成
      }, 3000)

      window.wx.ready(() => {
        clearTimeout(timeoutId)
        
        // 检查SDK是否真正可用
        if (sdkError) {
          console.warn('微信JS-SDK配置失败，跳过分享API调用')
          resolve({ success: false, finalShareData: finalShareData })
          return
        }
        
        // 验证关键API是否可用
        const hasNewAPI = typeof window.wx.updateAppMessageShareData === 'function' && 
                          typeof window.wx.updateTimelineShareData === 'function'
        const hasOldAPI = typeof window.wx.onMenuShareAppMessage === 'function' && 
                          typeof window.wx.onMenuShareTimeline === 'function'
        
        if (!hasNewAPI && !hasOldAPI) {
          console.warn('微信分享API不可用，跳过分享配置')
          resolve({ success: false, finalShareData: finalShareData })
          return
        }
        
        sdkConfigured = true

        try {
          console.log('微信JS-SDK准备就绪，开始配置分享...')

          // 准备分享配置数据，使用最终确定的分享数据（可能包含服务端返回的参数）
          const shareConfig = {
            title: finalShareData.title,
            desc: finalShareData.desc,
            link: finalShareData.link, // 直接使用最终确定的链接
            imgUrl: finalShareData.imgUrl
          }

          console.log('微信分享配置:', shareConfig)

          // 使用新版微信分享API
          if (typeof window.wx.updateAppMessageShareData === 'function') {
            console.log('使用新版分享API-enhanced')

            // 配置分享给朋友
            window.wx.updateAppMessageShareData({
              title: shareConfig.title,
              desc: shareConfig.desc,
              link: shareConfig.link,
              imgUrl: shareConfig.imgUrl,
              success: function() {
                console.log('✅ 新版接口: 分享给朋友设置成功', shareConfig)
                resolve({ success: true, finalShareData: finalShareData })
              },
              fail: function(res) {
                console.error('❌ 新版接口: 分享给朋友设置失败', res)
                
                // 记录详细的失败信息
                const failureDetails = {
                  api: 'updateAppMessageShareData',
                  shareConfig,
                  failureResponse: res,
                  timestamp: new Date().toISOString()
                }
                console.error('分享API调用失败详情:', failureDetails)
                
                // 上报错误
                try {
                  import('@/api/error').then(({ reportShareError }) => {
                    reportShareError('新版分享API调用失败', shareConfig, {
                      operation: 'updateAppMessageShareData',
                      url: window.location.href
                    }).catch(err => {
                      console.error('报告分享API错误失败:', err)
                    })
                  })
                } catch (importError) {
                  console.error('导入错误报告模块失败:', importError)
                }
                
                // 即使失败也认为配置完成，确保用户可以分享
                resolve({ success: true, finalShareData: finalShareData })
              }
            })

            // 配置分享到朋友圈
            if (typeof window.wx.updateTimelineShareData === 'function') {
              window.wx.updateTimelineShareData({
                title: shareConfig.title,
                link: shareConfig.link,
                imgUrl: shareConfig.imgUrl,
                success: function() {
                  console.log('✅ 新版接口: 分享到朋友圈设置成功', shareConfig)
                },
                fail: function(res) {
                  console.error('❌ 新版接口: 分享到朋友圈设置失败', res)
                  
                  // 记录详细的失败信息
                  const failureDetails = {
                    api: 'updateTimelineShareData',
                    shareConfig,
                    failureResponse: res,
                    timestamp: new Date().toISOString(),
                    sdkStatus: {
                      sdkConfigured,
                      sdkError,
                      wxExists: !!window.wx,
                      wxReady: !!window.wx && typeof window.wx.ready === 'function',
                      wxConfig: !!window.wx && typeof window.wx.config === 'function',
                      wxError: !!window.wx && typeof window.wx.error === 'function',
                      newShareAPI: !!window.wx && typeof window.wx.updateTimelineShareData === 'function',
                      oldShareAPI: !!window.wx && typeof window.wx.onMenuShareTimeline === 'function'
                    },
                    environment: {
                      isWechat: isWechatBrowser(),
                      userAgent: navigator.userAgent,
                      url: window.location.href,
                      timestamp: new Date().toISOString()
                    }
                  }
                  console.error('朋友圈分享API调用失败详情:', failureDetails)
                  
                  // 上报错误
                  try {
                    import('@/api/error').then(({ reportShareError }) => {
                      reportShareError('分享功能异常', shareConfig, {
                        operation: {
                          operation: 'updateTimelineShareData',
                          url: window.location.href
                        },
                        shareData: {
                          ...shareConfig,
                          hasTitle: !!shareConfig.title,
                          hasDesc: !!shareConfig.desc,
                          hasLink: !!shareConfig.link,
                          hasImgUrl: !!shareConfig.imgUrl
                        },
                        wechatInfo: {
                          isWechat: isWechatBrowser(),
                          isWechatDevTools: navigator.userAgent.includes('wechatdevtools'),
                          wechatVersion: (navigator.userAgent.match(/MicroMessenger\/([0-9.]+)/) || [])[1] || 'unknown',
                          netType: 'unknown',
                          language: navigator.language || 'unknown'
                        },
                        pageInfo: {
                          title: document.title,
                          url: window.location.href,
                          pathname: window.location.pathname,
                          search: window.location.search,
                          hash: window.location.hash,
                          referrer: document.referrer,
                          readyState: document.readyState,
                          visibilityState: document.visibilityState
                        },
                        wechatSDKInfo: failureDetails.sdkStatus,
                        performanceInfo: {
                          loadTime: performance.now(),
                          memoryUsage: performance.memory ? {
                            usedJSHeapSize: performance.memory.usedJSHeapSize,
                            totalJSHeapSize: performance.memory.totalJSHeapSize,
                            jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
                          } : null
                        },
                        debugTimestamp: Date.now(),
                        domInfo: {
                          metaTags: Array.from(document.querySelectorAll('meta')).map(meta => ({
                            name: meta.name || meta.property,
                            content: meta.content
                          })),
                          titleTag: document.title
                        }
                      }).catch(err => {
                        console.error('报告朋友圈分享API错误失败:', err)
                      })
                    })
                  } catch (importError) {
                    console.error('导入错误报告模块失败:', importError)
                  }
                }
              })
            } else {
              console.warn('新版朋友圈分享API不可用')
            }
          } else {
            console.log('使用旧版分享API-enhanced')
            
            // 配置分享给朋友
            window.wx.onMenuShareAppMessage({
              title: shareConfig.title,
              desc: shareConfig.desc,
              link: shareConfig.link,
              imgUrl: shareConfig.imgUrl,
              success: function() {
                console.log('✅ 旧版接口: 分享给朋友设置成功', shareConfig)
                resolve(true)
              },
              fail: function(res) {
                console.error('❌ 旧版接口: 分享给朋友设置失败', res)
                
                // 记录详细的失败信息
                const failureDetails = {
                  api: 'onMenuShareAppMessage',
                  shareConfig,
                  failureResponse: res,
                  timestamp: new Date().toISOString()
                }
                console.error('旧版分享API调用失败详情:', failureDetails)
                
                // 即使失败也认为配置完成，确保用户可以分享
                resolve(true)
              }
            })

            // 配置分享到朋友圈
            window.wx.onMenuShareTimeline({
              title: shareConfig.title,
              link: shareConfig.link,
              imgUrl: shareConfig.imgUrl,
              success: function() {
                console.log('✅ 旧版接口: 分享到朋友圈设置成功', shareConfig)
                resolve(true)
              },
              fail: function(res) {
                console.error('❌ 旧版接口: 分享到朋友圈设置失败', res)
                
                // 记录详细的失败信息
                const failureDetails = {
                  api: 'onMenuShareTimeline',
                  shareConfig,
                  failureResponse: res,
                  timestamp: new Date().toISOString()
                }
                console.error('旧版朋友圈分享API调用失败详情:', failureDetails)
                
                // 即使失败也认为配置完成，确保用户可以分享
                resolve(true)
              }
            })
          }
        } catch (shareConfigError) {
          console.error('配置微信分享时发生错误:', shareConfigError)
          
          // 上报配置错误
          try {
            import('@/api/error').then(({ reportShareError }) => {
              reportShareError('配置微信分享时发生错误', processedShareData, {
                operation: 'share_config_error',
                url: window.location.href,
                error: shareConfigError.message,
                stack: shareConfigError.stack,
                originalShareData: shareData,
                processedShareData: processedShareData
              }).catch(err => {
                console.error('报告配置错误失败:', err)
              })
            })
          } catch (importError) {
            console.error('导入错误报告模块失败:', importError)
          }
          
          // 即使出错也认为配置完成，确保用户可以分享
          resolve(true)
        }
        
      })

      // 添加错误处理
      window.wx.error((res) => {
        clearTimeout(timeoutId)
        console.error('微信JS-SDK配置失败:', res)
        
        // 设置错误状态
        sdkError = true

        // 特殊处理：判断是否是"成功"消息（微信开发工具的bug）
        if (res === '成功' || 
            (typeof res === 'string' && (res.includes('成功') || res.toLowerCase().includes('success')))) {
          console.log('检测到微信返回"成功"但调用了error回调，将其处理为成功')
          sdkError = false // 重置错误状态
          resolve({ success: true, finalShareData: finalShareData })
          return
        }

        // 在微信开发工具中，忽略配置错误
        if (navigator.userAgent.includes('wechatdevtools')) {
          console.warn('微信开发工具中忽略配置错误')
          sdkError = false // 重置错误状态
          resolve({ success: true, finalShareData: finalShareData })
          return
        }

        // 在开发环境中，忽略配置错误
        if (process.env.NODE_ENV === 'development') {
          console.warn('开发环境：忽略微信JS-SDK配置错误')
          sdkError = false // 重置错误状态
          resolve({ success: true, finalShareData: finalShareData })
          return
        }

        // 生产环境中，SDK配置失败时不调用分享API
        console.warn('微信JS-SDK配置失败，分享功能将受限')
        resolve({ success: false, finalShareData: finalShareData })
      })
    })
  } catch (error) {
    console.error('配置微信分享失败:', error)

    // 检查错误是否包含"成功"字样
    if (error && error.message && (
      error.message.includes('成功') || 
      error.message.toLowerCase().includes('success')
    )) {
      console.warn('捕获到包含"成功"的错误，尝试继续执行')
      return true
    }

    // 即使出错也返回成功，确保用户可以分享
    return true
  }
}

/**
 * 带重试机制的微信分享配置
 * @param {Object} shareData 分享数据
 * @param {number} maxRetries 最大重试次数
 * @returns {Promise<boolean>}
 */
export async function setupWechatShareWithRetry(shareData, maxRetries = 3) {
  // 检查是否可以初始化（传入分享数据进行比较）
  if (!canInitialize(shareData)) {
    console.log('跳过微信分享配置（防重复机制）')
    return { success: true, finalShareData: shareData } // 返回包含分享数据的对象
  }
  
  // 保存当前分享数据到全局状态，供监听器使用
  globalShareState.currentShareData = shareData
  console.log('已保存当前分享数据到全局状态:', shareData)

  // 如果有正在进行的初始化，返回该Promise
  if (globalShareState.initPromise) {
    console.log('返回正在进行的初始化Promise')
    return globalShareState.initPromise
  }

  // 标记初始化开始
  markInitializationStart()

  // 创建初始化Promise
  globalShareState.initPromise = (async () => {
    let retryCount = 0
    let finalShareData = shareData
    
    while (retryCount < maxRetries) {
      try {
        console.log(`尝试配置微信分享，第 ${retryCount + 1} 次`)
        
        const result = await setupWechatShareEnhanced(shareData)
        
        if (result && result.finalShareData) {
          console.log('微信分享配置成功，获取到最终分享数据:', result.finalShareData)
          finalShareData = result.finalShareData
          markInitializationComplete(true)
          return { success: true, finalShareData: finalShareData }
        } else if (result) {
          console.log('微信分享配置成功')
          markInitializationComplete(true)
          return { success: true, finalShareData: finalShareData }
        } else {
          throw new Error('分享配置返回失败')
        }
      } catch (error) {
        retryCount++
        console.error(`微信分享配置失败，第 ${retryCount} 次尝试:`, error.message)
        
        if (retryCount >= maxRetries) {
          console.error('微信分享配置达到最大重试次数，放弃重试')
          markInitializationComplete(false)
          // 即使达到最大重试次数也返回成功，确保用户可以分享
          return { success: true, finalShareData: finalShareData }
        }
        
        // 等待一段时间后重试，使用指数退避策略
        const delay = Math.pow(2, retryCount) * 1000
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
    
    markInitializationComplete(false)
    return { success: true, finalShareData: finalShareData }
  })()

  try {
    const result = await globalShareState.initPromise
    return result
  } finally {
    // 清除Promise引用
    globalShareState.initPromise = null
  }
}

/**
 * 自动初始化微信分享（增强版）
 * @returns {Promise<boolean>}
 */
export async function autoInitWechatShareEnhanced(externalShareData = null) {
  if (!isWechatBrowser()) {
    console.log('不在微信环境中，跳过自动分享初始化')
    return false
  }

  try {
    console.log('开始自动初始化微信分享（增强版）...', externalShareData ? '使用外部分享数据' : '使用默认分享数据')
    
    // 减少延迟时间，确保更快地初始化分享
    await new Promise(resolve => setTimeout(resolve, 500))
    
    // 智能清理URL，确保包含scene=1并移除动态参数
    console.log('=== 开始URL清理过程 ===')
    const cleanUrl = () => {
      const url = window.location.href
      console.log('原始完整URL:', url)
      
      const urlWithoutHash = url.split('#')[0]
      console.log('移除hash后的URL:', urlWithoutHash)
      
      if (url !== urlWithoutHash) {
        console.log('已移除的hash部分:', url.split('#')[1])
      }
      
      // 检查URL格式，修复多个问号的问题
      let cleanedUrl = urlWithoutHash
      console.log('准备检查URL格式...')
      
      // 如果URL中有多个问号，修复为正确格式
      const questionMarkCount = (cleanedUrl.match(/\?/g) || []).length
      console.log('URL中问号数量:', questionMarkCount)
      
      if (questionMarkCount > 1) {
        console.log('⚠️ 检测到URL中有多个问号，修复为正确格式')
        console.log('修复前URL:', cleanedUrl)
        
        // 保留第一个问号，将其他问号替换为&
        cleanedUrl = cleanedUrl.replace(/\?/g, (match, offset) => {
          return offset === cleanedUrl.indexOf('?') ? '?' : '&'
        })
        
        console.log('修复后URL:', cleanedUrl)
      } else {
        console.log('✅ URL格式正常，无需修复')
      }
      
      console.log('尝试使用URL对象进行参数处理...')
      try {
        const urlObj = new URL(cleanedUrl)
        console.log('✅ URL对象创建成功')
        console.log('URL基础部分:', urlObj.origin + urlObj.pathname)
        console.log('原始查询参数:', urlObj.search)
        
        // 记录原始参数
        const originalParams = {}
        urlObj.searchParams.forEach((value, key) => {
          originalParams[key] = value
        })
        console.log('原始参数对象:', originalParams)
        
        // 保留tm参数用于签名，只移除其他动态参数
        const dynamicParams = ['t', 'timestamp', '_t', '_timestamp', 'time']
        console.log('准备移除的动态参数:', dynamicParams)
        
        const removedParams = []
        dynamicParams.forEach(param => {
          if (urlObj.searchParams.has(param)) {
            const value = urlObj.searchParams.get(param)
            removedParams.push({ param, value })
            urlObj.searchParams.delete(param)
          }
        })
        


        
        // 确保包含scene=1参数
        urlObj.searchParams.set('scene', '1')
        
        const finalUrl = urlObj.toString()
        console.log('最终处理后的URL:', finalUrl)
        
        return finalUrl
      } catch (e) {
        console.error('❌ URL解析失败，使用字符串处理方式:', e)
        // 字符串处理方式
        let cleanUrl = cleanedUrl
        console.log('开始字符串处理方式...')
        
        // 先进行URL解码，避免双重编码
        try {
          const decodedUrl = decodeURI(cleanUrl)
          console.log('URL解码成功:', decodedUrl)
          cleanUrl = decodedUrl
        } catch (decodeError) {
          console.warn('⚠️ URL解码失败，使用原始URL:', decodeError)
        }
        
        // 记录调试信息
        const debugInfo = {
          originalUrl: cleanedUrl,
          step1: '清理前的URL',
          urlAfterHashRemoval: cleanUrl
        }
        console.log('字符串处理调试信息 - 步骤1:', debugInfo)
        
        // 移除动态参数，但保留tm参数用于签名
        const beforeParamRemoval = cleanUrl
        cleanUrl = cleanUrl.replace(/[&?](t|timestamp|_t|_timestamp|time|v)=\d+/g, '')
          .replace(/\?&/, '?')
          .replace(/&$/, '')
        
        if (beforeParamRemoval !== cleanUrl) {
          console.log('动态参数移除前:', beforeParamRemoval)
          console.log('动态参数移除后:', cleanUrl)
        } else {
          console.log('✅ 无动态参数需要移除')
        }
        
        debugInfo.step2 = '移除动态参数后'
        debugInfo.urlAfterParamRemoval = cleanUrl
        console.log('字符串处理调试信息 - 步骤2:', debugInfo)
        
        // 确保包含scene=1
        if (cleanUrl.includes('?')) {
          cleanUrl += '&scene=1'
        } else {
          cleanUrl += '?scene=1'
        }
        
        debugInfo.step3 = '添加scene参数后'
        debugInfo.urlAfterSceneAdd = cleanUrl
        
        // 确保没有多余的?或&
        cleanUrl = cleanUrl.replace(/\?$/, '')
          .replace(/\?&/, '?')
          .replace(/&&/g, '&')
          .replace(/$/, '')
        
        debugInfo.step4 = '清理后'
        debugInfo.urlAfterCleanup = cleanUrl
        
        // 如果处理后没有查询参数，添加scene=1
        if (!cleanUrl.includes('?')) {
          cleanUrl += '?scene=1'
        }
        
        debugInfo.finalUrl = cleanUrl
        debugInfo.userAgent = navigator.userAgent
        debugInfo.timestamp = new Date().toISOString()
        
        // 发送调试信息到后端
        setTimeout(async () => {
          try {
            await logShareData({
              title: '蒙古丽人商城',
              desc: '专注于蒙古服饰设计、开发、推广。',
              link: cleanUrl,
              imgUrl: 'https://mall.sainbainu.com/static/images/sharelogo.png',
              debugInfo,
              type: 'url_cleanup_debug_autoinit'
            })
          } catch (logError) {
            console.error('发送调试信息失败:', logError)
          }
        }, 0)
        
        return cleanUrl
      }
    }

    // 根据当前页面路径动态设置默认分享配置
    const getPageSpecificShareData = () => {
      const pathname = window.location.pathname
      const cleanedUrl = cleanUrl() // 智能清理URL，保留有用参数
      
      // 根据页面路径设置不同的分享内容
      if (pathname === '/category') {
        return {
          title: '蒙古丽人 - 企业介绍',
          desc: '内蒙古丽人文化传媒有限公司，定制热线：133 4714 1793，专注于蒙古服饰设计、制作、加工和销售，传承和发扬蒙古民族文化。',
          link: cleanedUrl,
          imgUrl: 'https://mall.sainbainu.com/static/images/sharelogo.png'
        }
      } else if (pathname.startsWith('/product/')) {
        return {
          title: '蒙古丽人商城 - 商品详情',
          desc: '专注于蒙古服饰设计、开发、推广。定制热线：133 4714 1793',
          link: cleanedUrl,
          imgUrl: 'https://mall.sainbainu.com/static/images/sharelogo.png'
        }
      } else if (pathname === '/products') {
        return {
          title: '蒙古丽人商城 - 商品列表',
          desc: '专注于蒙古服饰设计、开发、推广。定制热线：133 4714 1793',
          link: cleanedUrl,
          imgUrl: 'https://mall.sainbainu.com/static/images/sharelogo.png'
        }
      } else {
        // 默认首页配置
        return {
          title: '蒙古丽人商城',
          desc: '专注于蒙古服饰设计、开发、推广。定制热线：133 4714 1793',
          link: cleanedUrl,
          imgUrl: 'https://mall.sainbainu.com/static/images/sharelogo.png'
        }
      }
    }
    
    // 优先使用外部传入的分享数据，否则使用页面特定的默认数据
    const finalShareData = externalShareData || getPageSpecificShareData()
    
    console.log('自动初始化分享配置:', finalShareData)
    console.log('数据来源:', externalShareData ? '外部传入' : '页面默认')
    
    // 检查是否可以初始化（传入分享数据进行比较）
    if (!canInitialize(finalShareData)) {
      console.log('跳过自动分享初始化（防重复机制）')
      return { success: true, finalShareData: finalShareData }
    }
    
    // 关键修改：先更新元标签，再配置分享
    updateMetaTags({
      title: finalShareData.title,
      description: finalShareData.desc,
      image: finalShareData.imgUrl,
      url: finalShareData.link
    })
    
    // 确保分享数据被传递到服务端进行处理
    console.log('传递给setupWechatShareWithRetry的分享数据:', finalShareData)
    const result = await setupWechatShareWithRetry(finalShareData)
    
    if (result) {
      console.log('微信分享自动初始化成功')
    } else {
      console.warn('微信分享自动初始化失败')
    }
    
    return result
  } catch (error) {
    console.error('自动初始化微信分享失败:', error)
    return false
  }
}

/**
 * 设置订单页面的分享信息
 * @param {Object} orderData 订单分享数据
 * @returns {Promise<boolean>}
 */
export async function setupOrderShare(orderData) {
  if (!isWechatBrowser()) {
    console.warn('不在微信浏览器环境中，无需配置分享')
    return false
  }

  try {
    console.log('开始配置订单分享:', orderData)
    
    // 使用增强版分享配置
    const result = await setupWechatShareEnhanced(orderData)
    
    if (result) {
      console.log('订单分享配置成功')
    } else {
      console.warn('订单分享配置失败')
    }
    
    return result
  } catch (error) {
    console.error('订单分享配置失败:', error)
    return false
  }
}

/**
 * 监听页面可见性变化，重新初始化分享
 */
export function initShareOnVisibilityChange() {
  if (!isWechatBrowser()) {
    return
  }

  // 防止重复添加监听器
  if (globalShareState.visibilityListenerAdded) {
    return
  }
  globalShareState.visibilityListenerAdded = true

  // 监听页面可见性变化
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      console.log('页面重新可见，检查是否需要重新初始化分享')
      
      // 只有在超过5分钟未初始化时才重新初始化
      const fiveMinutes = 5 * 60 * 1000
      const now = Date.now()
      
      if (!globalShareState.isInitialized || (now - globalShareState.lastInitTime) > fiveMinutes) {
        console.log('页面可见性变化，重新初始化分享')
        // 延迟执行，确保页面完全加载
        setTimeout(() => {
          // 使用保存的分享数据，如果没有则使用默认数据
          const shareDataToUse = globalShareState.currentShareData
          console.log('页面可见性变化，使用的分享数据:', shareDataToUse ? '已保存的数据' : '默认数据')
          
          autoInitWechatShareEnhanced(shareDataToUse).then(result => {
            console.log('页面可见性变化后分享初始化结果:', result)
          }).catch(error => {
            console.error('页面可见性变化后分享初始化失败:', error)
          })
        }, 2000) // 增加延迟时间，避免频繁调用
      } else {
        console.log('分享已在5分钟内初始化过，跳过重新初始化')
      }
    }
  })
}

/**
 * 初始化URL变化监听，当URL变化时重新初始化微信SDK
 * 解决路由守卫添加tm参数导致的签名失效问题
 */
export function initUrlChangeListener() {
  if (!isWechatBrowser()) {
    console.log('不在微信环境中，跳过URL变化监听')
    return
  }

  // 防止重复添加监听器
  if (globalShareState.urlChangeListenerAdded) {
    console.log('URL变化监听器已添加，跳过重复添加')
    return
  }

  console.log('添加URL变化监听器')
  
  // 记录初始URL
  globalShareState.lastUrl = window.location.href
  console.log('初始URL:', globalShareState.lastUrl)

  // 监听popstate事件（浏览器前进后退）
  window.addEventListener('popstate', handleUrlChange)
  
  // 监听pushstate和replacestate事件（程序化路由变化）
  const originalPushState = history.pushState
  const originalReplaceState = history.replaceState
  
  history.pushState = function(...args) {
    originalPushState.apply(history, args)
    handleUrlChange()
  }
  
  history.replaceState = function(...args) {
    originalReplaceState.apply(history, args)
    handleUrlChange()
  }
  
  // 使用MutationObserver监听DOM变化（可能的URL变化）
  const observer = new MutationObserver(() => {
    if (window.location.href !== globalShareState.lastUrl) {
      handleUrlChange()
    }
  })
  
  observer.observe(document, { 
    childList: true, 
    subtree: true, 
    attributes: true, 
    attributeFilter: ['href'] 
  })
  
  // 定期检查URL变化（兜底方案）
  const urlCheckInterval = setInterval(() => {
    if (window.location.href !== globalShareState.lastUrl) {
      handleUrlChange()
    }
  }, 1000)
  
  // 清理函数
  window.addEventListener('beforeunload', () => {
    clearInterval(urlCheckInterval)
    observer.disconnect()
  })
  
  globalShareState.urlChangeListenerAdded = true
  console.log('URL变化监听器添加成功')
}

/**
 * 处理URL变化事件
 */
function handleUrlChange() {
  const currentUrl = window.location.href
  
  if (currentUrl === globalShareState.lastUrl) {
    return // URL没有变化
  }
  
  // 分析URL变化类型
  const oldUrlObj = new URL(globalShareState.lastUrl || window.location.href)
  const newUrlObj = new URL(currentUrl)
  
  // 检查是否只是tm参数的变化
  const oldTm = oldUrlObj.searchParams.get('tm')
  const newTm = newUrlObj.searchParams.get('tm')
  const isTmOnlyChange = oldTm !== newTm && 
    oldUrlObj.pathname === newUrlObj.pathname && 
    oldUrlObj.hash === newUrlObj.hash
  
  console.log('检测到URL变化:')
  console.log('  旧URL:', globalShareState.lastUrl)
  console.log('  新URL:', currentUrl)
  console.log('  变化类型:', isTmOnlyChange ? 'tm参数变化' : '路径或其他参数变化')
  
  // 清理相关缓存，避免使用旧的配置数据
  if (globalShareState.lastUrl && currentUrl !== globalShareState.lastUrl) {
    console.log('🧹 清理URL变化相关的缓存')
    
    // 清理微信配置缓存
    const oldNormalizedUrl = normalizeUrlForWechat(globalShareState.lastUrl)
    const newNormalizedUrl = normalizeUrlForWechat(currentUrl)
    
    if (oldNormalizedUrl !== newNormalizedUrl) {
      // 清理全局配置缓存
      if (globalShareState.configCache.has(oldNormalizedUrl)) {
        globalShareState.configCache.delete(oldNormalizedUrl)
        console.log('已清理旧URL的配置缓存:', oldNormalizedUrl)
      }
      
      // 清理window级别的缓存
      if (window.wechatConfigCache && window.wechatConfigCache[oldNormalizedUrl]) {
        delete window.wechatConfigCache[oldNormalizedUrl]
        console.log('已清理window级别的配置缓存:', oldNormalizedUrl)
      }
      
      // 清理正在进行的请求
      if (globalShareState.pendingRequests.has(oldNormalizedUrl)) {
        globalShareState.pendingRequests.delete(oldNormalizedUrl)
        console.log('已清理旧URL的待处理请求:', oldNormalizedUrl)
      }
    }
    
    // 重置相关状态
    globalShareState.isInitializing = false
    globalShareState.lastSuccessfulUrl = ''
    globalShareState.lastSuccessfulTime = 0
  }
  
  // 更新记录的URL
  globalShareState.lastUrl = currentUrl
  
  // 清除之前的防抖定时器
  if (globalShareState.urlChangeTimeout) {
    clearTimeout(globalShareState.urlChangeTimeout)
  }
  
  // 如果是tm参数变化，立即处理（不使用防抖）
  if (isTmOnlyChange) {
    console.log('tm参数变化，立即重新初始化微信分享...')
    handleTmParameterChange(currentUrl)
    return
  }
  
  // 其他类型的URL变化使用防抖处理
  const debounceDelay = 500
  
  // 防抖处理，避免频繁重新初始化
  globalShareState.urlChangeTimeout = setTimeout(async () => {
    try {
      console.log('URL变化检测到，重新初始化微信JS-SDK...')
      
      // 重置初始化状态，允许重新初始化
      globalShareState.isInitialized = false
      globalShareState.isInitializing = false
      
      // 重新初始化微信JS-SDK，这会重新请求 /api/wechat/js-config 接口
      const initResult = await initWechatJsSDKEnhanced(currentUrl)
      
      if (initResult) {
        console.log('URL变化后微信JS-SDK重新初始化成功')
        
        // 等待一小段时间让SDK完全就绪，然后设置分享数据
        setTimeout(async () => {
          try {
            // 使用保存的分享数据，如果没有则使用默认数据
            const shareDataToUse = globalShareState.currentShareData
            console.log('URL变化后设置分享数据，使用的分享数据:', shareDataToUse ? '已保存的数据' : '默认数据')
            
            await autoInitWechatShareEnhanced(shareDataToUse)
            console.log('URL变化后分享数据设置完成')
          } catch (shareError) {
            console.error('URL变化后设置分享数据失败:', shareError)
          }
        }, 500)
      } else {
        console.warn('URL变化后微信JS-SDK重新初始化失败')
      }
    } catch (error) {
      console.error('URL变化后重新初始化微信JS-SDK失败:', error)
      
      // 发送错误日志
      try {
        await logShareData({
          title: '微信JS-SDK重新初始化失败',
          desc: 'URL变化后重新初始化失败',
          link: currentUrl,
          imgUrl: 'https://mall.sainbainu.com/static/images/sharelogo.png',
          debugInfo: {
            error: error.message,
            stack: error.stack,
            oldUrl: globalShareState.lastUrl,
            newUrl: currentUrl,
            changeType: 'other_change',
            timestamp: new Date().toISOString()
          },
          type: 'url_change_reinit_error'
        })
      } catch (logError) {
        console.error('发送URL变化错误日志失败:', logError)
      }
    }
  }, debounceDelay)
}

/**
 * 处理tm参数变化的专用函数
 * 立即响应，不使用防抖，提供多重重试策略
 */
async function handleTmParameterChange(newUrl) {
  const startTime = Date.now()
  console.log('🔄 开始处理tm参数变化，新URL:', newUrl)
  
  // 策略配置
  const strategies = [
    {
      name: '快速重新初始化',
      action: async () => {
        // 重置状态并快速重新初始化
        globalShareState.isInitialized = false
        globalShareState.isInitializing = false
        
        // 使用保存的分享数据，如果没有则使用默认数据
        const shareDataToUse = globalShareState.currentShareData
        console.log('快速重新初始化，使用的分享数据:', shareDataToUse ? '已保存的数据' : '默认数据')
        
        await autoInitWechatShareEnhanced(shareDataToUse)
      }
    },
    {
      name: '使用简化URL重新初始化',
      action: async () => {
        const simpleUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`
        console.log('使用简化URL:', simpleUrl)
        globalShareState.isInitialized = false
        globalShareState.isInitializing = false
        
        // 使用保存的分享数据，如果没有则使用默认数据
        const shareDataToUse = globalShareState.currentShareData
        console.log('简化URL重新初始化，使用的分享数据:', shareDataToUse ? '已保存的数据' : '默认数据')
        
        await autoInitWechatShareEnhanced(shareDataToUse)
      }
    },
    {
      name: '强制重置后重新初始化',
      action: async () => {
        // 强制重置所有状态
        globalShareState.isInitialized = false
        globalShareState.isInitializing = false
        globalShareState.lastInitUrl = ''
        globalShareState.lastInitTime = 0
        
        // 等待200ms让状态完全重置
        await new Promise(resolve => setTimeout(resolve, 200))
        
        // 使用保存的分享数据，如果没有则使用默认数据
        const shareDataToUse = globalShareState.currentShareData
        console.log('强制重置后重新初始化，使用的分享数据:', shareDataToUse ? '已保存的数据' : '默认数据')
        
        await autoInitWechatShareEnhanced(shareDataToUse)
      }
    },
    {
      name: '延迟重新初始化',
      action: async () => {
        console.log('等待1秒让路由完全稳定...')
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        globalShareState.isInitialized = false
        globalShareState.isInitializing = false
        
        // 使用保存的分享数据，如果没有则使用默认数据
        const shareDataToUse = globalShareState.currentShareData
        console.log('URL变化重新初始化，使用的分享数据:', shareDataToUse ? '已保存的数据' : '默认数据')
        
        await autoInitWechatShareEnhanced(shareDataToUse)
      }
    }
  ]
  
  // 逐个尝试策略
  for (let i = 0; i < strategies.length; i++) {
    const strategy = strategies[i]
    
    try {
      console.log(`🔧 策略${i + 1}: ${strategy.name}`)
      await strategy.action()
      
      const duration = Date.now() - startTime
      console.log(`✅ 策略${i + 1}成功，耗时: ${duration}ms`)
      
      // 验证分享功能
      if (await verifyWechatShareFunction()) {
        console.log('✅ 微信分享功能验证通过')
        return true
      } else {
        console.warn('⚠️ 微信分享功能验证失败，但策略执行成功')
        return true
      }
      
    } catch (error) {
      console.warn(`策略${i + 1}失败:`, error.message || error)
      
      // 如果不是最后一个策略，继续尝试下一个
      if (i < strategies.length - 1) {
        console.log(`继续尝试下一个策略...`)
        continue
      }
    }
  }
  
  const duration = Date.now() - startTime
  console.error(`所有策略都失败，tm参数变化处理失败，总耗时: ${duration}ms`)
  return false
}

/**
 * 验证微信分享功能是否正常
 */
async function verifyWechatShareFunction() {
  try {
    if (!window.wx) {
      console.log('wx对象不存在')
      return false
    }
    
    if (!window.wx.updateTimelineShareData || !window.wx.updateAppMessageShareData) {
      console.log('微信分享API不存在')
      return false
    }
    
    // 检查全局状态
    if (!globalShareState.isInitialized) {
      console.log('全局状态显示未初始化')
      return false
    }
    
    console.log('微信分享功能验证通过')
    return true
    
  } catch (error) {
    console.error('验证微信分享功能时出错:', error)
    return false
  }
}

export default {
  loadWechatJsSDKEnhanced,
  getWechatJsConfigEnhanced,
  initWechatJsSDKEnhanced,
  setupWechatShareEnhanced,
  setupWechatShareWithRetry,
  autoInitWechatShareEnhanced,
  setupOrderShare,
  initShareOnVisibilityChange,
  initUrlChangeListener
}
